import type { ChangeEvent, HTMLAttributes } from 'react'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import type { Props } from './CheckboxStyle'
import CheckboxStyle, { CheckboxIconStyle, CheckboxWrapperStyle } from './CheckboxStyle'
import Checkmark from '../../assets/icons/checkmark.svg'
import Indeterminate from '../../assets/icons/indeterminate.svg'

const Checkbox = forwardRef<HTMLInputElement, Props & HTMLAttributes<HTMLInputElement>>(
  (
    { checked: checkedProp, className, onChange, disabled, error, indeterminate, ...otherProps },
    externalRef,
  ) => {
    const [checked, setChecked] = useState(!!checkedProp)
    const ref = useRef<HTMLInputElement>(null)

    useImperativeHandle(externalRef, () => ref.current as HTMLInputElement)

    useEffect(() => {
      if (ref.current) {
        ref.current.indeterminate = indeterminate ?? false
      }
    }, [ref, indeterminate])

    // Make the component aware of changes in the checked prop
    useMemo(() => {
      setChecked(!!checkedProp)
    }, [checkedProp, setChecked])

    return (
      <CheckboxWrapperStyle
        className={className}
        $disabled={disabled}
        $checked={checked}
        $error={error}
        aria-disabled={disabled}
      >
        <CheckboxIconStyle
          $checked={checked}
          $indeterminate={indeterminate}
          data-testid="checkboxIcon"
          size={15}
        >
          {!checked && indeterminate && <Indeterminate />}
          {checked && <Checkmark />}
        </CheckboxIconStyle>
        <CheckboxStyle
          disabled={disabled}
          checked={checked}
          ref={ref}
          {...otherProps}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (onChange) {
              onChange(e)
            }
            if (typeof checkedProp === 'undefined') {
              setChecked(!checked)
            }
          }}
        />
      </CheckboxWrapperStyle>
    )
  },
)
Checkbox.displayName = 'Checkbox'

export default Checkbox
